/**
 * SessionList Component
 *
 * Left panel list of import sessions with selection highlighting.
 */

import {
  People as PeopleIcon,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  List,
  ListItemButton,
  Skeleton,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { SessionSummary } from '../types';

// =============================================================================
// Helpers
// =============================================================================

function formatSessionDate(isoDate: string): string {
  const date = new Date(isoDate);
  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = date.getDate();
  const time = date
    .toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    .toLowerCase();
  return `${month} ${day} - ${time}`;
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

type ChipColor = 'warning' | 'success' | 'error' | 'info' | 'default';

function statusColor(status: string): ChipColor {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
    case 'cancelled':
      return 'error';
    case 'ready_for_review':
      return 'warning';
    default:
      return 'info';
  }
}

/** Maps chip color names to theme palette paths */
const STATUS_PALETTE_MAP: Record<ChipColor, string> = {
  success: 'success.main',
  error: 'error.main',
  warning: 'warning.main',
  info: 'info.main',
  default: 'text.disabled',
};

function fileTypeLabel(fileType: string): string {
  return fileType.toUpperCase();
}

// =============================================================================
// Component
// =============================================================================

interface SessionListProps {
  sessions: SessionSummary[];
  selectedSessionId: string | null;
  onSelect: (id: string) => void;
  isLoading: boolean;
}

export const SessionList: React.FC<SessionListProps> = ({
  sessions,
  selectedSessionId,
  onSelect,
  isLoading,
}) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <List disablePadding>
        {Array.from({ length: 3 }).map((_, i) => (
          <ListItemButton key={i} sx={{ py: 2, px: 2 }}>
            <Box sx={{ width: '100%' }}>
              <Skeleton width="50%" height={20} />
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Skeleton width={60} height={20} />
                <Skeleton width={70} height={20} />
              </Box>
            </Box>
          </ListItemButton>
        ))}
      </List>
    );
  }

  if (sessions.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No sessions found.
        </Typography>
      </Box>
    );
  }

  return (
    <List disablePadding>
      {sessions.map((session) => {
        const isSelected = session.session_id === selectedSessionId;
        const color = statusColor(session.status);
        const palettePath = STATUS_PALETTE_MAP[color];
        // Resolve the actual color value from the theme
        const borderColor = palettePath.split('.').reduce<unknown>(
          (obj, key) => (obj as Record<string, unknown>)?.[key],
          theme.palette,
        ) as string;

        return (
          <ListItemButton
            key={session.session_id}
            selected={isSelected}
            onClick={() => onSelect(session.session_id)}
            sx={{
              py: 1.5,
              px: 2,
              borderLeft: `3px solid ${isSelected ? borderColor : 'transparent'}`,
              transition: 'border-color 0.15s ease, background-color 0.15s ease',
              '&:hover': {
                borderLeftColor: alpha(borderColor, 0.5),
              },
              '&.Mui-selected': {
                bgcolor: alpha(borderColor, 0.06),
                '&:hover': {
                  bgcolor: alpha(borderColor, 0.1),
                },
              },
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2" fontWeight={isSelected ? 600 : 500}>
                {formatSessionDate(session.created_at)}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 0.5,
                }}
              >
                <Chip
                  label={statusLabel(session.status)}
                  color={color}
                  size="small"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
                {session.total_patients > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}
                  >
                    <PeopleIcon sx={{ fontSize: 13 }} />
                    {session.total_patients}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ ml: 'auto', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}
                >
                  {fileTypeLabel(session.file_type)}
                </Typography>
              </Box>
            </Box>
          </ListItemButton>
        );
      })}
    </List>
  );
};
